import { isSameDay, isValid, isWithinInterval } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import DatePicker from 'react-datepicker';
import { bind } from '../Utils/Bind';
import { componentAutoReaction } from '../Utils/MobxTools';
import { IFormField } from '../Utils/PrimitiveFormField';
import { BsXCircle } from '@react-icons/all-files/bs/BsXCircle';
import {addUnitsOfTime} from "../Utils/DateFns";

export interface IDateRangePickerProps {
    readonly dateFormat?: string;
    readonly timeFormat?: string;
    readonly showTimeSelect?: boolean;
    readonly showTimeSelectOnly?: boolean;
    readonly input?: boolean; // what
    readonly startDate: IFormField<Date | undefined>;
    readonly endDate: IFormField<Date | undefined>;
    readonly minDate?: Date;
    readonly maxDate?: Date;
    readonly disabled?: boolean;
    readonly required?: boolean;
    readonly validation?: string;
    readonly timezone?: string;
    readonly className?: string;
    readonly hideClear?: boolean;
    onChange?(v: number[]): void;
}

@observer
export class DateRangePicker extends React.Component<IDateRangePickerProps> {
    public constructor(props: IDateRangePickerProps) {
        super(props);

        componentAutoReaction(
            this,
            () => this.validations,
            v => {
                this.props.startDate.onMount(v);
                this.props.endDate.onMount(v);
            }
        );
    }

    public render() {
        let dateFormat: string | undefined;

        if (this.props.dateFormat) {
            dateFormat = this.props.dateFormat;
        } else if (this.props.showTimeSelect && this.props.showTimeSelectOnly) {
            if (this.props.timeFormat) {
                dateFormat = this.props.timeFormat;
            } else {
                dateFormat = 'h:mm aa';
            }
        } else if (this.props.showTimeSelect) {
            dateFormat = 'MMMM d, yyyy h:mm aa';
        }

        return (
            <div className={this.props.className}>
                <DatePicker
                    dateFormat={dateFormat}
                    timeFormat={this.props.timeFormat}
                    selected={this.startDate}
                    showTimeSelect={this.props.showTimeSelect}
                    showTimeSelectOnly={this.props.showTimeSelectOnly && this.props.showTimeSelect}
                    className={this.props.className + ' form-control'}
                    placeholderText={`Start ${this.placeholderText}`}
                    onChange={this.onStartDateChange}
                    disabled={this.props.disabled}
                    required={this.props.required || this.props.endDate.value != null}
                    dayClassName={this.dayClass}
                    minDate={this.props.minDate}
                    maxDate={this.props.endDate.value}
                />
                <DatePicker
                    dateFormat={dateFormat}
                    timeFormat={this.props.timeFormat}
                    selected={this.endDate}
                    showTimeSelect={this.props.showTimeSelect}
                    showTimeSelectOnly={this.props.showTimeSelectOnly && this.props.showTimeSelect}
                    className={this.props.className + ' form-control'}
                    placeholderText={`End ${this.placeholderText}`}
                    disabled={this.props.disabled}
                    required={this.props.required || this.props.startDate.value != null}
                    onChange={this.onEndDateChange}
                    dayClassName={this.dayClass}
                    minDate={this.props.startDate.value}
                    maxDate={this.props.maxDate}
                />
                {!this.props.hideClear && (
                    <button onClick={this.clear} disabled={this.props.disabled} className="btn btn-link">
                        <BsXCircle color="red" size="12px" />
                    </button>
                )}
                {this.props.startDate.submitted &&
                    this.props.startDate.errors.map((e, idx) => (
                        <span className="form-control-feedback" key={idx}>
                            {e}
                        </span>
                    ))}
                {this.props.endDate.submitted &&
                    this.props.endDate.errors.map((e, idx) => (
                        <span className="form-control-feedback" key={idx}>
                            {e}
                        </span>
                    ))}
            </div>
        );
    }

    @computed
    private get startDate() {
        const value = this.props.startDate.value;
        if (value == null) return null;
        if (!this.props.timezone) return value;

        return utcToZonedTime(value, this.props.timezone);
    }

    @computed
    private get endDate() {
        const value = this.props.endDate.value;
        if (value == null) return null;
        if (!this.props.timezone) return value;

        return utcToZonedTime(value, this.props.timezone);
    }

    @bind
    @action
    private onStartDateChange(date: Date | null, event: React.SyntheticEvent) {
        if (date && isValid(date)) {
            if (this.props.timezone) date = zonedTimeToUtc(date, this.props.timezone);

            this.props.startDate.onChange(date);
            if (this.props.onChange) {
                this.props.onChange([date.getTime(), this.props.endDate.value?.getTime() ?? date.getTime()]);
            }
        }
    }

    @bind
    @action
    private onEndDateChange(date: Date | null, event: React.SyntheticEvent) {
        let monthInFuture = addUnitsOfTime(new Date(), 1, 'month');
        if (date && isValid(date) && date < monthInFuture) {
            if (this.props.timezone) date = zonedTimeToUtc(date, this.props.timezone);

            this.props.endDate.onChange(date);
            if (this.props.onChange) {
                this.props.onChange([this.props.startDate.value?.getTime() ?? date.getTime(), date.getTime()]);
            }
        }
    }

    @bind
    private dayClass(date: Date): string | null {
        if (this.props.startDate.value && this.props.endDate.value) {
            if (this.props.startDate.value > this.props.endDate.value) {
                if (isSameDay(this.props.startDate.value, date) || isSameDay(this.props.endDate.value, date)) {
                    return 'invalid-date-range';
                }
                return '';
            } else {
                if (isSameDay(this.props.startDate.value, date) || isSameDay(this.props.endDate.value, date)) {
                    return 'react-datepicker__day--selected';
                } else if (
                    isWithinInterval(date, { start: this.props.startDate.value, end: this.props.endDate.value })
                ) {
                    return 'activeDay';
                }
            }
        }

        return null;
    }

    @bind
    private clear(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        this.props.startDate.onChange(undefined);
        this.props.endDate.onChange(undefined);
    }

    @computed
    private get validations() {
        const rules = new Set<string>(this.props.validation?.split('|'));
        if (this.props.required) rules.add('required');

        return Array.from(rules).join('|');
    }

    @computed
    private get placeholderText() {
        if (this.props.showTimeSelect && this.props.showTimeSelectOnly) {
            return 'Time';
        } else if (this.props.showTimeSelect) {
            return 'Date/Time';
        } else {
            return 'Date';
        }
    }
}
